import React, { useState, useEffect } from 'react';
import axios from 'axios';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const GLSettings = () => {
  const [settings, setSettings] = useState(null);
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const [formData, setFormData] = useState({
    ap_invoices_account_vuid: '',
    ap_retainage_account_vuid: '',
    wages_payable_account_vuid: '',
    cash_account_vuid: '',
    project_expenses_account_vuid: '',
    insurance_recovery_account_vuid: '',
    accrued_insurance_account_vuid: '',
    ar_invoices_account_vuid: '',
    ar_retainage_account_vuid: '',
    revenue_account_vuid: '',
    cost_in_excess_of_billing_account_vuid: '',
    billing_in_excess_of_cost_account_vuid: '',
    ap_invoice_integration_method: 'invoice',
    ar_invoice_integration_method: 'invoice',
    labor_cost_integration_method: 'actuals',
    payment_integration_method: 'journal_entry',
    payments_enabled: false,
    description: 'Default GL Settings'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch GL Settings
      const settingsResponse = await axios.get(`${baseURL}/api/gl-settings`);
            if (settingsResponse.data && settingsResponse.data.length > 0) {
        const existingSettings = settingsResponse.data[0];
                setSettings(existingSettings);
        setFormData({
          ap_invoices_account_vuid: existingSettings.ap_invoices_account_vuid || '',
          ap_retainage_account_vuid: existingSettings.ap_retainage_account_vuid || '',
          wages_payable_account_vuid: existingSettings.wages_payable_account_vuid || '',
          cash_account_vuid: existingSettings.cash_account_vuid || '',
          project_expenses_account_vuid: existingSettings.project_expenses_account_vuid || '',
          insurance_recovery_account_vuid: existingSettings.insurance_recovery_account_vuid || '',
          accrued_insurance_account_vuid: existingSettings.accrued_insurance_account_vuid || '',
          ar_invoices_account_vuid: existingSettings.ar_invoices_account_vuid || '',
          ar_retainage_account_vuid: existingSettings.ar_retainage_account_vuid || '',
          revenue_account_vuid: existingSettings.revenue_account_vuid || '',
          cost_in_excess_of_billing_account_vuid: existingSettings.cost_in_excess_of_billing_account_vuid || '',
          billing_in_excess_of_cost_account_vuid: existingSettings.billing_in_excess_of_cost_account_vuid || '',
          ap_invoice_integration_method: existingSettings.ap_invoice_integration_method || 'invoice',
          ar_invoice_integration_method: existingSettings.ar_invoice_integration_method || 'invoice',
          labor_cost_integration_method: existingSettings.labor_cost_integration_method || 'actuals',
          payment_integration_method: existingSettings.payment_integration_method || 'journal_entry',
          payments_enabled: existingSettings.payments_enabled || false,
          description: existingSettings.description || 'Default GL Settings'
        });
              }
      
      // Fetch Chart of Accounts
      const accountsResponse = await axios.get(`${baseURL}/api/chartofaccounts`);
                  setChartOfAccounts(accountsResponse.data || []);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage('Error loading data. Please try again.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      if (settings) {
        // Update existing settings
                        const updateResponse = await axios.put(`${baseURL}/api/gl-settings/${settings.vuid}`, formData);
                        // Update settings state immediately with the response
        const updatedSettings = updateResponse.data;
        setSettings(updatedSettings);
        // Also update formData from the response to ensure consistency
        setFormData({
          ap_invoices_account_vuid: updatedSettings.ap_invoices_account_vuid || '',
          ap_retainage_account_vuid: updatedSettings.ap_retainage_account_vuid || '',
          wages_payable_account_vuid: updatedSettings.wages_payable_account_vuid || '',
          ar_invoices_account_vuid: updatedSettings.ar_invoices_account_vuid || '',
          ar_retainage_account_vuid: updatedSettings.ar_retainage_account_vuid || '',
          revenue_account_vuid: updatedSettings.revenue_account_vuid || '',
          cost_in_excess_of_billing_account_vuid: updatedSettings.cost_in_excess_of_billing_account_vuid || '',
          billing_in_excess_of_cost_account_vuid: updatedSettings.billing_in_excess_of_cost_account_vuid || '',
          project_expenses_account_vuid: updatedSettings.project_expenses_account_vuid || '',
          insurance_recovery_account_vuid: updatedSettings.insurance_recovery_account_vuid || '',
          accrued_insurance_account_vuid: updatedSettings.accrued_insurance_account_vuid || '',
          cash_account_vuid: updatedSettings.cash_account_vuid || '',
          payment_integration_method: updatedSettings.payment_integration_method || 'journal_entry',
          payments_enabled: updatedSettings.payments_enabled || false,
          ap_invoice_integration_method: updatedSettings.ap_invoice_integration_method || 'invoice',
          ar_invoice_integration_method: updatedSettings.ar_invoice_integration_method || 'invoice',
          labor_cost_integration_method: updatedSettings.labor_cost_integration_method || 'actuals',
          description: updatedSettings.description || 'Default GL Settings'
        });
        setMessage('GL Settings updated successfully!');
        setMessageType('success');
      } else {
        // Create new settings
        const response = await axios.post(`${baseURL}/api/gl-settings`, formData);
        setSettings(response.data);
        setMessage('GL Settings created successfully!');
        setMessageType('success');
      }
      
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage(error.response?.data?.error || 'Error saving settings. Please try again.');
      setMessageType('error');
    } finally {
      setSaving(false);
    }
  };

  const renderAccountOptions = (types) => {
    if (!chartOfAccounts || chartOfAccounts.length === 0) {
      return <option value="" disabled>No accounts available. Please add accounts in Chart of Accounts.</option>;
    }
    const active = chartOfAccounts.filter(account => account.status === 'active');
    const filtered = active.filter(account => types.includes(account.account_type));
    const rows = filtered.length > 0 ? filtered : active;
    const showType = filtered.length === 0;
    return rows.map(account => (
      <option key={account.vuid} value={account.vuid}>
        {account.account_number} - {account.account_name}{showType ? ` (${account.account_type})` : ''}
      </option>
    ));
  };

  const accountRows = [
    { name: 'ap_invoices_account_vuid', label: 'AP Invoices', types: ['Liability'], required: true, hint: 'Default account for Accounts Payable invoices' },
    { name: 'ap_retainage_account_vuid', label: 'AP Retainage', types: ['Liability'], hint: 'Default account for AP retainage withholdings' },
    { name: 'wages_payable_account_vuid', label: 'Wages Payable', types: ['Liability'], hint: 'Default account for labor costs. If not set, AP Invoices is used.' },
    { name: 'cash_account_vuid', label: 'Cash', types: ['Asset'], hint: 'Default cash account for payment journal entries' },
    { name: 'project_expenses_account_vuid', label: 'Project Expenses', types: ['Liability'], hint: 'Default credit account for project expense journal entries' },
    { name: 'insurance_recovery_account_vuid', label: 'Insurance Recovery', types: ['Liability', 'Revenue', 'Income'], hint: 'Credit account for GL Insurance recovery. A negative recovery posts as a debit.' },
    { name: 'accrued_insurance_account_vuid', label: 'Accrued Insurance', types: ['Liability'], hint: 'Credit account for GL Insurance accrual' },
    { name: 'ar_invoices_account_vuid', label: 'AR Invoices', types: ['Asset'], required: true, hint: 'Default account for Accounts Receivable invoices' },
    { name: 'ar_retainage_account_vuid', label: 'AR Retainage', types: ['Asset'], hint: 'Default account for AR retainage withholdings' },
    { name: 'revenue_account_vuid', label: 'Revenue', types: ['Revenue', 'Income'], required: true, hint: 'Default account for project billing revenue credits' },
    { name: 'cost_in_excess_of_billing_account_vuid', label: 'Cost in Excess of Billing', types: ['Asset'], hint: 'Default account for costs exceeding billings' },
    { name: 'billing_in_excess_of_cost_account_vuid', label: 'Billing in Excess of Cost', types: ['Liability'], hint: 'Default account for billings exceeding costs' },
  ];

  const selectClass = 'w-full px-2 py-1 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vermillion-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading GL Settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900">GL Settings</h1>
          <p className="mt-1 text-sm text-gray-600">
            Configure default Chart of Accounts for financial transactions
          </p>
        </div>

        {message && (
          <div className={`mb-4 px-3 py-2 rounded text-sm ${
            messageType === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">Accounts</h2>
            </div>
            <div className="budget-lines-grid">
              <table className="budget-lines-table">
                <thead>
                  <tr>
                    <th>Setting</th>
                    <th>Account</th>
                  </tr>
                </thead>
                <tbody>
                  {accountRows.map((row) => (
                    <tr key={row.name}>
                      <td className="whitespace-nowrap" title={row.hint}>
                        <label htmlFor={row.name} className="font-medium">
                          {row.label}{row.required ? ' *' : ''}
                        </label>
                      </td>
                      <td>
                        <select
                          id={row.name}
                          name={row.name}
                          value={formData[row.name]}
                          onChange={handleInputChange}
                          className={selectClass}
                          required={row.required || undefined}
                        >
                          <option value="">Select Account</option>
                          {renderAccountOptions(row.types)}
                        </select>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className="whitespace-nowrap">
                      <label htmlFor="payments_enabled" className="font-medium">Payments</label>
                    </td>
                    <td>
                      <label htmlFor="payments_enabled" className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          id="payments_enabled"
                          name="payments_enabled"
                          checked={formData.payments_enabled}
                          onChange={(e) => setFormData(prev => ({ ...prev, payments_enabled: e.target.checked }))}
                          className="h-4 w-4 border-gray-300 rounded"
                        />
                        Enable payments against AP invoices
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td className="whitespace-nowrap">
                      <label htmlFor="description" className="font-medium">Description</label>
                    </td>
                    <td>
                      <input
                        type="text"
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        className={selectClass}
                        placeholder="Description of these GL settings"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-4">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">Integration</h2>
            </div>
            <div className="budget-lines-grid">
              <table className="budget-lines-table">
                <thead>
                  <tr>
                    <th>Setting</th>
                    <th>Method</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="whitespace-nowrap">
                      <label htmlFor="ap_invoice_integration_method" className="font-medium">AP Invoice</label>
                    </td>
                    <td>
                      <select id="ap_invoice_integration_method" name="ap_invoice_integration_method" value={formData.ap_invoice_integration_method} onChange={handleInputChange} className={selectClass}>
                        <option value="invoice">Invoice Records</option>
                        <option value="journal_entries">Journal Entries</option>
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td className="whitespace-nowrap">
                      <label htmlFor="ar_invoice_integration_method" className="font-medium">AR Invoice</label>
                    </td>
                    <td>
                      <select id="ar_invoice_integration_method" name="ar_invoice_integration_method" value={formData.ar_invoice_integration_method} onChange={handleInputChange} className={selectClass}>
                        <option value="invoice">Invoice Records</option>
                        <option value="journal_entries">Journal Entries</option>
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td className="whitespace-nowrap">
                      <label htmlFor="labor_cost_integration_method" className="font-medium">Labor Cost</label>
                    </td>
                    <td>
                      <select id="labor_cost_integration_method" name="labor_cost_integration_method" value={formData.labor_cost_integration_method} onChange={handleInputChange} className={selectClass}>
                        <option value="actuals">Actuals (Use Labor Cost Amount)</option>
                        <option value="charge_rate">Charge Rate (Employee Rate × Hours)</option>
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td className="whitespace-nowrap">
                      <label htmlFor="payment_integration_method" className="font-medium">Payment</label>
                    </td>
                    <td>
                      <select id="payment_integration_method" name="payment_integration_method" value={formData.payment_integration_method} onChange={handleInputChange} className={selectClass}>
                        <option value="journal_entry">Journal Entries</option>
                        <option value="payment">Payment Records</option>
                        <option value="do_not_integrate">Do Not Integrate</option>
                      </select>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button type="submit" disabled={saving} className="btn-claret">
              {saving ? 'Saving...' : (settings ? 'Update Settings' : 'Create Settings')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GLSettings;
